import type { Deal, DocumentRef, EnrichmentResult, RegistryLookup, Leader, TeamMemberBio, IndustryDetail, BusinessDetail, IpoDetails, IpoHistory, PeerDetail, TaskStatus, TaskType, TaskPriority, CreditRating, ResearchNote, ResearchKind, CallsTrend, AgencyRatings, Probe42Data, ModuleRatingsAI } from '@krit/core';

/**
 * API base. In dev it's '' so calls hit /api/* and Vite proxies to the Worker.
 * In production set VITE_API_BASE to the Worker origin (the SPA on Pages calls it cross-origin).
 */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

/**
 * Thin fetch wrapper for the Worker API. Sends cookies (Cloudflare Access session).
 * Auto-retries a 503 (the retryable "Auth store unreachable" when the DB pooler is momentarily
 * saturated under concurrent load) a couple of times with backoff, so a transient blip doesn't
 * surface to the user as an API error. The 503 is raised in auth BEFORE the handler runs, so
 * re-sending — including POSTs — is side-effect-free.
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const ATTEMPTS = 6; // ride out transient Supabase pooler blips (was 3 → gave up in ~1s)
  let lastStatus = 0, lastErr = '';
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${BASE}/api${path}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
        ...init,
      });
    } catch (e) {
      // A dropped connection (pooler refused / network blip) throws instead of returning 503 —
      // treat it the same: back off and retry (auth runs before the handler, so retry is safe).
      lastErr = String((e as Error).message);
      if (attempt < ATTEMPTS - 1) { await sleep(400 * (attempt + 1)); continue; }
      throw new Error('Network error — the server was briefly unreachable. Please retry.');
    }
    if (res.ok) return res.json() as Promise<T>;
    lastStatus = res.status;
    // 503 = auth store (DB pooler) momentarily unreachable — raised before the handler, so retrying
    // (incl. POSTs) is side-effect-free. Back off with a longer schedule than before.
    if (res.status === 503 && attempt < ATTEMPTS - 1) { await sleep(400 * (attempt + 1)); continue; }
    const body = await res.json().catch(() => ({}));
    const err = new Error((body as { error?: string }).error ?? `Request failed (${res.status})`) as Error & { status?: number };
    err.status = res.status; // callers can branch on this (e.g. 403 = "no access" vs. a real failure)
    throw err;
  }
  throw new Error(lastErr || `Request failed (${lastStatus})`);
}

export interface Identity {
  email: string;
  role: string;
  memberId: string | null;
  name: string;
}

/** Roles the API accepts for writes (mirrors requireRole('admin','partner','analyst') server-side). */
export const EDIT_ROLES = new Set(['admin', 'partner', 'analyst']);
export const canEdit = (id?: Identity | null): boolean => EDIT_ROLES.has(id?.role ?? '');
/**
 * Admin-only affordances: the archive list, restoring, and permanent delete. Purely cosmetic —
 * the server enforces the same rule, so hiding a button is convenience, not security.
 */
export const isAdmin = (id?: Identity | null): boolean => id?.role === 'admin';

export interface DealComputed {
  score: number;
  grade: string;
  thesisFit: 'Fit' | 'Borderline' | 'Pass';
  entryPE: number | null;
  mos: number | null;
  peNow: number;
  peIPO: number;
  baseMOIC: number | null;
  recommendation: string;
  credit: CreditRating;
}

/** `archivedAt` is only sent by GET /deals/:id — list endpoints already exclude archived deals. */
export type DealDto = Deal & { computed: DealComputed; archivedAt?: string | null };

/** A row of the admin archive: the deal plus who archived it and when. */
export type ArchivedDealDto = DealDto & { archivedAt: string; archivedBy: string | null };

export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  email: string | null;
  role: string;
  active: boolean;
}

export interface Settings {
  id: string;
  fundName: string;
  icThreshold: number;
  intakeNumber: string | null;
  weights: Record<string, number>;
  sectors: Record<string, { pe: number }>;
}

export const getMe = () => api<{ identity: Identity }>('/me');
export interface NotificationItem { id: string; type: 'mention' | 'ic_vote' | 'diligence' | 'intake' | 'task'; title: string; detail: string; dealId: string | null; dealShort: string | null; ts: string; }
export const getNotifications = () => api<{ notifications: NotificationItem[] }>('/notifications');
export const getHealth = () => api<{ ok: boolean; env: string }>('/health');
export const listDeals = () => api<{ deals: DealDto[] }>('/deals');
// ── tl;dv "Unmatched calls" inbox ──────────────────────────────────────────
export interface InboxCall { id: string; title: string; date: string; participants: string; duration: string; status: string; recordingUrl: string; hasTranscript: boolean; summary: string; }
export const callsInbox = () => api<{ calls: InboxCall[] }>('/calls/inbox');
export const linkCallToDeal = (callId: string, dealId: string) =>
  api<{ ok: boolean }>(`/calls/${callId}/link`, { method: 'POST', body: JSON.stringify({ dealId }) });
export interface CallMatchSuggestion { callId: string; dealId: string; company: string; method: string; }
export const rematchInbox = () =>
  api<{ checked: number; suggestions: CallMatchSuggestion[] }>('/calls/inbox/rematch', { method: 'POST' });
export const listIntake =() => api<{ deals: DealDto[] }>('/deals?status=Intake');
export const getDeal = (id: string) => api<{ deal: DealDto }>(`/deals/${id}`);
export const createDeal = (body: Partial<Deal>) =>
  api<{ deal: Deal }>('/deals', { method: 'POST', body: JSON.stringify(body) });
export const patchDeal = (id: string, patch: Partial<Deal>) =>
  api<{ deal: Deal }>(`/deals/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
/**
 * Archive a deal — the "remove it" action everyone gets. Reversible: the deal leaves every list
 * but nothing is destroyed, and an admin can restore it from the archive.
 */
export const archiveDeal = (id: string) =>
  api<{ ok: boolean; archivedAt: string }>(`/deals/${id}/archive`, { method: 'POST' });
/** Put an archived deal back in the pipeline. Admin only, server-side. */
export const unarchiveDeal = (id: string) =>
  api<{ ok: boolean }>(`/deals/${id}/unarchive`, { method: 'POST' });
/** The archive. Admin only, server-side — 403s for everyone else. */
export const listArchivedDeals = () => api<{ deals: ArchivedDealDto[] }>('/deals/archived');
/**
 * Permanently delete a deal. Admin only, server-side. Cascades to its calls, notes, documents,
 * discussion and IC votes, and there is no undo — non-admins archive instead.
 */
export const deleteDeal = (id: string) =>
  api<{ ok: boolean }>(`/deals/${id}`, { method: 'DELETE' });
/** Upload a deck/document to a deal — stores the file (Supabase Storage) + extracted text. */
export async function uploadDocument(dealId: string, file: File, text: string, pages: number): Promise<{ document: DocumentRef }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('text', text);
  fd.append('pages', String(pages));
  fd.append('type', file.type || '');
  const res = await fetch(`${BASE}/api/deals/${dealId}/documents`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? `Upload failed (${res.status})`); }
  return res.json() as Promise<{ document: DocumentRef }>;
}
export const deleteDocument = (dealId: string, docId: string) =>
  api<{ ok: boolean }>(`/deals/${dealId}/documents/${docId}`, { method: 'DELETE' });
/** Attach a data room to an existing deal: upload a ZIP → Drive folder + intel.dataroom. */
// Cloudflare Pages Functions cap the request body at 100 MB, and the whole zip is held in Worker
// memory while it's unzipped and each file streamed to Drive. Beyond ~90 MB the upload stalls at the
// edge and never completes (the browser just spins) — so reject it up front with a clear message
// instead of letting the user wait 15 minutes for a silent failure. Big rooms go via WhatsApp (n8n
// on the VPS, no size limit) or split into <80 MB parts.
const ZIP_MAX_BYTES = 90 * 1024 * 1024;
export async function uploadDataroomZip(dealId: string, file: File): Promise<{ ok: boolean; added: number; total: number; folderUrl: string }> {
  if (file.size > ZIP_MAX_BYTES) {
    const mb = Math.round(file.size / (1024 * 1024));
    throw new Error(`This zip is ${mb} MB — too large to upload through the app (limit ~90 MB). Forward it via WhatsApp with the deal name in the caption (no size limit there), or split it into parts under 80 MB and upload each.`);
  }
  const fd = new FormData();
  fd.append('zip', file);
  const res = await fetch(`${BASE}/api/deals/${dealId}/dataroom/upload-zip`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? `Data-room upload failed (${res.status})`); }
  return res.json() as Promise<{ ok: boolean; added: number; total: number; folderUrl: string }>;
}
/** Save re-extracted text for a stored document (client re-reads the raw file + OCRs a scan). */
export const saveDocumentText = (dealId: string, docId: string, text: string, pages?: number) =>
  api<{ ok: boolean; chars: number }>(`/deals/${dealId}/documents/${docId}/text`, { method: 'PUT', body: JSON.stringify({ text, pages }) });
/** Authenticated download URL (browser sends the Access cookie); file is streamed privately. */
export const documentDownloadUrl = (dealId: string, docId: string) => `${BASE}/api/deals/${dealId}/documents/${docId}/download`;
// ── Screener sync (latest filings → Documents, top earnings call → Calls) ────
export interface ScreenerSyncResult { company: string; listed: boolean; screenerName: string | null; filingsAdded: number; callsAdded: number; notes: string[]; }
/** Pull the deal company's latest Screener filings + most recent earnings call (or its listed peers' if unlisted). Idempotent. */
export const screenerSync = (dealId: string) => api<ScreenerSyncResult>(`/deals/${dealId}/screener/sync`, { method: 'POST' });
/** Synthesise the underlying industry trend across all of a deal's earnings calls (own + competitors') → deals.callsTrend. */
export const synthesizeCallsTrend = (dealId: string) => api<{ trend: CallsTrend }>(`/deals/${dealId}/calls/industry-trend`, { method: 'POST' });
/** Progress-tracking synthesis across ONLY the deal's own management/founder calls — distinct from
 *  synthesizeCallsTrend, which is deliberately cross-company (own + competitors). */
export const synthesizeInternalCallsTrend = (dealId: string) => api<{ trend: CallsTrend }>(`/deals/${dealId}/calls/internal-trend`, { method: 'POST' });
/** Fetch PUBLISHED credit ratings (CRISIL/ICRA/CARE/…) for the deal's company from the public domain → deals.intel.agencyRatings. */
export const fetchAgencyRatings = (dealId: string) => api<{ agencyRatings: AgencyRatings }>(`/deals/${dealId}/agency-ratings`, { method: 'POST' });
/** OCR scanned/image deck pages (rendered client-side to data-URLs) with a vision model → plain text. */
export const ocrDeckImages = (images: string[]) => api<{ text: string; pages: number }>('/ocr', { method: 'POST', body: JSON.stringify({ images }) });
export interface CallInput { title: string; date?: string; participants?: string; duration?: string; transcript?: string; recordingUrl?: string; }
export const addCall = (id: string, body: CallInput) =>
  api<{ call: unknown; usedLLM: boolean }>(`/deals/${id}/calls`, { method: 'POST', body: JSON.stringify(body) });
export const analyseCallApi = (id: string, callId: string) =>
  api<{ call: unknown; usedLLM: boolean }>(`/deals/${id}/calls/${callId}/analyse`, { method: 'POST' });
export interface CallMeta { title?: string; date?: string; participants?: string; duration?: string; }
/** Upload an audio recording (multipart) → async transcription. Poll getCallStatus to finish. */
export async function uploadCallRecording(id: string, file: File, meta: CallMeta): Promise<{ call: unknown }> {
  const fd = new FormData();
  fd.append('audio', file);
  for (const k of ['title', 'date', 'participants', 'duration'] as const) if (meta[k]) fd.append(k, meta[k]!);
  const res = await fetch(`${BASE}/api/deals/${id}/calls/upload`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? `Upload failed (${res.status})`);
  }
  return res.json() as Promise<{ call: unknown }>;
}
/** Poll a transcribing call; server finishes transcription + analysis when ready. */
export const getCallStatus = (id: string, callId: string) =>
  api<{ call: unknown; state: string; error?: string }>(`/deals/${id}/calls/${callId}/status`);
export const deleteCall = (id: string, callId: string) =>
  api<{ ok: boolean }>(`/deals/${id}/calls/${callId}`, { method: 'DELETE' });
export const addNote = (id: string, text: string) =>
  api<{ note: { u: string; t: string; at: string } }>(`/deals/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
// ── Deal discussion (flat channel + reply threads) ───────────────────────────
export interface DiscussionAuthor { id: string | null; name: string; initials: string }
export interface DiscussionAttachment { id: string; name: string; type: string | null; size: number | null }
export interface DiscussionReaction { emoji: string; count: number; mine: boolean; who: string[] }
export interface DiscussionMessage {
  id: string;
  parentId: string | null;
  author: DiscussionAuthor;
  body: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  mine: boolean;
  canEdit: boolean;
  mentions: { id: string; name: string }[];
  reactions: DiscussionReaction[];
  attachments: DiscussionAttachment[];
  replies: DiscussionMessage[];
}
export const getDiscussion = (dealId: string) =>
  api<{ messages: DiscussionMessage[] }>(`/deals/${dealId}/discussion`);
export const postDiscussion = (
  dealId: string,
  body: { body: string; parentId?: string | null; mentions?: string[]; attachmentIds?: string[] },
) => api<{ message: DiscussionMessage }>(`/deals/${dealId}/discussion`, { method: 'POST', body: JSON.stringify(body) });
export const editDiscussion = (msgId: string, body: { body: string; mentions?: string[] }) =>
  api<{ message: DiscussionMessage }>(`/discussion/${msgId}`, { method: 'PATCH', body: JSON.stringify(body) });
export const deleteDiscussion = (msgId: string) =>
  api<{ message: DiscussionMessage }>(`/discussion/${msgId}`, { method: 'DELETE' });
export const reactDiscussion = (msgId: string, emoji: string) =>
  api<{ message: DiscussionMessage }>(`/discussion/${msgId}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) });
/** Mark my @mentions read — all of them, or just the given messages. */
export const readDiscussionMentions = (messageIds?: string[]) =>
  api<{ ok: boolean; marked: number }>('/discussion/mentions/read', { method: 'POST', body: JSON.stringify({ messageIds }) });
/** Upload one attachment; reference the returned id in the next postDiscussion call. */
export async function uploadDiscussionAttachment(dealId: string, file: File): Promise<{ attachment: DiscussionAttachment }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${BASE}/api/deals/${dealId}/discussion/attachments`, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? `Upload failed (${res.status})`); }
  return res.json() as Promise<{ attachment: DiscussionAttachment }>;
}
/** Authenticated URL for an attachment (images render inline, other types download). */
export const discussionAttachmentUrl = (dealId: string, attId: string) =>
  `${BASE}/api/deals/${dealId}/discussion/attachments/${attId}`;
// ── Deal tasks / to-dos ──────────────────────────────────────────────────────
export interface DealTask {
  id: string;
  dealId: string;
  title: string;
  detail: string | null;
  assignee: { id: string; name: string; initials: string } | null;
  status: TaskStatus;
  type: TaskType;
  priority: TaskPriority;
  tags: string[];
  dueDate: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  deal?: { id: string; short: string; company: string }; // set only by the cross-deal My Tasks list
}
export type TaskInput = Partial<{
  title: string; detail: string | null; assigneeId: string | null;
  status: TaskStatus; type: TaskType; priority: TaskPriority; tags: string[]; dueDate: string | null;
}>;
export const getDealTasks = (dealId: string) => api<{ tasks: DealTask[] }>(`/deals/${dealId}/tasks`);
export const createTask = (dealId: string, body: TaskInput) =>
  api<{ task: DealTask }>(`/deals/${dealId}/tasks`, { method: 'POST', body: JSON.stringify(body) });
export const patchTask = (taskId: string, patch: TaskInput) =>
  api<{ task: DealTask }>(`/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteTaskApi = (taskId: string) =>
  api<{ ok: boolean }>(`/tasks/${taskId}`, { method: 'DELETE' });
/** Cross-deal list for the My Tasks view: scope 'mine' (assigned to me or on a deal I own) or 'all'. */
export const getMyTasks = (opts: { scope?: 'mine' | 'all'; done?: boolean } = {}) =>
  api<{ tasks: DealTask[] }>(`/tasks?scope=${opts.scope ?? 'mine'}${opts.done ? '&done=1' : ''}`);

// ── Research repository (searchable in-house knowledge) ──────────────────────
export type { ResearchNote, ResearchKind };
export interface ResearchInput { title?: string; body?: string; kind?: ResearchKind; sector?: string | null; tags?: string[]; sourceUrl?: string | null; dealId?: string | null; pinned?: boolean; }
export const getResearch = (opts: { q?: string; sector?: string; tag?: string; dealId?: string } = {}) => {
  const p = new URLSearchParams();
  if (opts.q) p.set('q', opts.q);
  if (opts.sector) p.set('sector', opts.sector);
  if (opts.tag) p.set('tag', opts.tag);
  if (opts.dealId) p.set('dealId', opts.dealId);
  const qs = p.toString();
  return api<{ notes: ResearchNote[] }>(`/research${qs ? `?${qs}` : ''}`);
};
export const getResearchFacets = () => api<{ sectors: string[]; tags: string[]; count: number }>('/research/facets');
export const createResearch = (b: ResearchInput) => api<{ note: ResearchNote }>('/research', { method: 'POST', body: JSON.stringify(b) });
export const patchResearch = (id: string, b: ResearchInput) => api<{ note: ResearchNote }>(`/research/${id}`, { method: 'PATCH', body: JSON.stringify(b) });
export const deleteResearch = (id: string) => api<{ ok: boolean }>(`/research/${id}`, { method: 'DELETE' });

export interface Holding {
  invested: number; entryDate: string; entryVal: number; mark: number;
  ownership: number; ipoStage: string; ipoPct: number; milestone: string;
}
export interface PortfolioHolding { id: string; short: string; company: string; sector: string; status: string; holding: Holding; baseMOIC: number | null; }
export interface Rollups { count: number; invested: number; mark: number; blendedMOIC: number | null; avgIpoReadiness: number; }
export interface Update { id: string; headline: string; sector: string | null; category: string | null; note: string | null; date: string | null; companyDealId: string | null; }
export interface NewsItem { id: string; headline: string; source: string | null; url: string | null; publishedAt: string | null; category: string | null; sector: string | null; }

export const getPortfolio = () => api<{ holdings: PortfolioHolding[]; rollups: Rollups }>('/portfolio');
export const getUpdates = () => api<{ updates: Update[] }>('/updates');
export const addUpdate = (u: Partial<Update>) => api<{ update: Update }>('/updates', { method: 'POST', body: JSON.stringify(u) });
export const getNews = () => api<{ news: NewsItem[] }>('/news');
export const refreshNewsApi = () =>
  api<{ ok: boolean; feeds: number; scanned: number; inserted: number; pruned: number }>('/news/refresh', { method: 'POST' });
export interface PeerFundamental { name: string; ticker: string; about?: string | null; price?: number | null; marketCap: number | null; pe: number | null; revenue: number | null; netProfit: number | null; netMargin: number | null; ratios?: { label: string; value: string }[]; ipo?: IpoDetails | null; ipoHist?: IpoHistory | null; }
/**
 * Real listed-peer fundamentals from screener.in (cached 24h server-side). IPO details
 * (Chittorgarh) are folded in by default so one fetch fills the whole table.
 */
export const peersLookup = (names: string[], withIpo = true) =>
  api<{ peers: PeerFundamental[] }>('/peers/lookup', { method: 'POST', body: JSON.stringify({ names, withIpo }) });
export interface IpoDetail extends IpoDetails { name: string; }
/** IPO year / lead managers / P-E-at-listing from Chittorgarh (recent IPOs ~2007+; blank for older). */
export const peersIpoDetails = (names: string[]) =>
  api<{ details: IpoDetail[] }>('/peers/ipo-details', { method: 'POST', body: JSON.stringify({ names }) });
/** Lazy per-peer drill-down: About + 3–5yr trend + IPO journey + recent headlines. */
export const peersDetail = (name: string, ticker?: string) =>
  api<{ detail: PeerDetail }>('/peers/detail', { method: 'POST', body: JSON.stringify({ name, ticker }) });
export interface AuditEntry { id: string; action: string; entity: string; entityId: string | null; diff: Record<string, unknown> | null; ts: string; actor: string | null; }
export const getAudit = (limit = 150) => api<{ audit: AuditEntry[] }>(`/audit?limit=${limit}`);
export const getTeam = () => api<{ team: TeamMember[] }>('/team');
export const createTeamMember = (b: { name: string; email?: string; role?: string; active?: boolean }) =>
  api<{ member: TeamMember }>('/team', { method: 'POST', body: JSON.stringify(b) });
export const updateTeamMember = (id: string, patch: Partial<{ name: string; email: string; role: string; active: boolean }>) =>
  api<{ member: TeamMember }>(`/team/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
export const deleteTeamMember = (id: string) =>
  api<{ ok: boolean }>(`/team/${id}`, { method: 'DELETE' });
export interface DeckTables {
  team: { name: string; role: string; bg: string }[];
  customers: { name: string; pct: number; note?: string }[];
  products: { name: string; pct: number; margin: number }[];
  pnl: { y: string; rev: number; eb: number; pat: number }[];
}
export interface DeckCoverage { scalars: string[]; team: number; customers: number; products: number; pnl: number; missing: string[]; warnings: string[]; }
export interface ExtractResult { fields: Record<string, string | number>; tables: DeckTables; coverage: DeckCoverage; }
export const extractDeal = (text: string) =>
  api<ExtractResult>('/extract-deal', { method: 'POST', body: JSON.stringify({ text }) });
/** Vision extraction for a SCANNED deck — reads tables straight from the page images. */
export const extractDealFromImages = (images: string[]) =>
  api<ExtractResult>('/extract-deal', { method: 'POST', body: JSON.stringify({ images }) });
export interface EnrichResult {
  fields: Record<string, string | number>;
  peers: string[];
  peerRows: unknown[];
  sources: string[];
  creditsUsed: number;
}
export const enrichDeal = (body: { company: string; sector?: string; location?: string; dealId?: string }) =>
  api<EnrichResult>('/enrich-deal', { method: 'POST', body: JSON.stringify(body) });
// ── Async enrichment agent (kick off → poll) ─────────────────────────────────
export interface EnrichRunStatus { runId: string; status: 'running' | 'done' | 'error'; step: string | null; toolCalls: number; creditsUsed: number; tokensUsed: number; result: EnrichmentResult | null; error: string | null; }
export const startEnrich = (dealId: string, body: { company: string; sector?: string; location?: string }) =>
  api<{ runId: string }>(`/deals/${dealId}/enrich`, { method: 'POST', body: JSON.stringify(body) });
export const enrichStatus = (dealId: string, runId: string) =>
  api<EnrichRunStatus>(`/deals/${dealId}/enrich/status?runId=${encodeURIComponent(runId)}`);
// ── Auto-populate foreground passes (client-sequenced; each its own request) ──
/**
 * Open-ended web-research dossier → persists deals.research. Foreground, runs to completion.
 * `fresh` (forced refresh) bypasses the 24h server cache and re-fetches — normal opens are cached & free.
 */
export const researchFetch = (dealId: string, fresh = false) =>
  api<{ sections: number; sources: number }>(`/deals/${dealId}/research`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** Build (or refresh) the deck-mined company briefing (timeline, product table, plans, key numbers, open questions) → deals.businessBrief. */
export const briefingFetch = (dealId: string, fresh = false) =>
  api<{ sections: number; products?: number; skipped?: string }>(`/deals/${dealId}/briefing`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** Per-person team research: background, tenure, other directorships, news, adverse + promoter view + synthesis → deals.teamResearch. */
export const teamResearchFetch = (dealId: string, fresh = false) =>
  api<{ people: number; adverse?: number; skipped?: string }>(`/deals/${dealId}/team-research`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** Deck-derived "questions to ask the promoter" (categorized, excludes deck-answered) → deals.promoterQuestions. */
export const questionsFetch = (dealId: string, fresh = false) =>
  api<{ categories: number; questions?: number; skipped?: string }>(`/deals/${dealId}/questions`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** LLM overall read (score + pros/cons + rationale) over the full enriched deal → deals.llmScore. */
export const scoreFetch = (dealId: string, fresh = false) =>
  api<{ score?: number; verdict?: string; pros?: number; cons?: number; skipped?: string }>(`/deals/${dealId}/score`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** AI-suggested Krit Score module ratings + per-module reasons + a team-facing "Krit note" —
 *  fills the on-screen sliders (still needs Save), never writes deals.ratings directly. */
export const moduleRatingsFetch = (dealId: string, fresh = true) =>
  api<{ moduleRatingsAI: ModuleRatingsAI }>(`/deals/${dealId}/module-ratings`, { method: 'POST', body: JSON.stringify({ fresh }) });
/** What one financials extraction produced — the statements written plus the fields it filled. */
export interface FinancialsExtractResult {
  filled: string[];
  statements: { years: number; pnl: number; balanceSheet: number; cashFlow: number; cells: number } | null;
  source: string;
  fromDeck: boolean;
  notes?: string[];
}
/**
 * Extract the detailed statements (P&L + balance sheet + cash flow) and headline figures from a
 * financials document → writes deals.financials and the deck-level fields derived from it.
 * Pass `text` for a file the browser just parsed; pass nothing to re-extract from the deal's
 * stored data-room documents (the deck is the server-side fallback).
 */
export const financialsExtract = (dealId: string, opts: { text?: string; source?: string; docId?: string } = {}) =>
  api<FinancialsExtractResult>(`/deals/${dealId}/financials`, { method: 'POST', body: JSON.stringify(opts) });
/**
 * Re-extract one data-room file into the deal. Google Sheets/Docs are read server-side (returns
 * `filled`); PDFs return `{ needsClient: true }` — the caller then downloads the file via
 * `dataroomFileRawUrl`, extracts text (pdfjs / OCR) in the browser, and posts it to `applyDataroomText`.
 */
export const reextractDataroomFile = (dealId: string, fileId: string) =>
  api<{ needsClient?: boolean; filled?: string[]; name?: string; note?: string; mimeType?: string; added?: number }>(`/deals/${dealId}/dataroom/extract-file`, { method: 'POST', body: JSON.stringify({ fileId }) });
/** Apply browser-extracted text (PDF via pdfjs, or OCR of a scanned page) from a data-room file into the deal. */
export const applyDataroomText = (dealId: string, fileId: string, text: string) =>
  api<{ filled: string[] }>(`/deals/${dealId}/dataroom/apply-text`, { method: 'POST', body: JSON.stringify({ fileId, text }) });
/** URL to stream a binary data-room file (PDF) from the Shared Drive — fetch with credentials: 'include'. */
export const dataroomFileRawUrl = (dealId: string, fileId: string) =>
  `${BASE}/api/deals/${dealId}/dataroom/file?fileId=${encodeURIComponent(fileId)}`;
/** Read EVERY server-readable data-room file (Sheets/Docs/zip) into the deal in one pass. */
export const extractAllDataroom = (dealId: string) =>
  api<{ filled: string[]; files: number; added: number; needOcr?: number }>(`/deals/${dealId}/dataroom/extract-all`, { method: 'POST', body: '{}' });
/** Get just the text of one data-room file (server-side; PDFs return needsClient for browser OCR). */
export const dataroomFileText = (dealId: string, fileId: string) =>
  api<{ text?: string; needsClient?: boolean; name?: string; added?: number }>(`/deals/${dealId}/dataroom/file-text`, { method: 'POST', body: JSON.stringify({ fileId }) });
/** Apply the whole room's gathered text in one pass: store docs, extract fields, re-discover peers. */
export const applyDataroomBulk = (dealId: string, docs: { fileId?: string; name?: string; text: string }[]) =>
  api<{ storedDocs: number; background?: boolean; filled?: string[]; peers?: number }>(`/deals/${dealId}/dataroom/apply-bulk`, { method: 'POST', body: JSON.stringify({ docs }) });
// ── AI chat assistant ─────────────────────────────────────────────────────
export interface ChatTurn { role: 'user' | 'assistant'; content: string; usedDeal?: string | null }
/**
 * Ask the AI assistant. Pass a dealId as the DEFAULT context; the server auto-detects a deal
 * NAMED in the message ("in Greenjoules, …") and answers about that one instead — `usedDeal`
 * reports which deal (if any) the answer was grounded in. Omit dealId + name nothing → portfolio.
 */
export const chatAsk = (message: string, opts?: { dealId?: string; history?: ChatTurn[] }) =>
  api<{ answer: string; usedDeal?: string | null }>('/chat', { method: 'POST', body: JSON.stringify({ message, dealId: opts?.dealId, history: opts?.history ?? [] }) });
/**
 * Resolve listed-peer fundamentals (+ IPO), discovering comparables when the deal has no seeds.
 * Options: `fresh` bypasses the 24h screener cache (expensive live re-scrape); `overwrite` re-runs
 * discovery and replaces an existing peer set. Pass a bare boolean for the legacy `fresh` form.
 */
export const peersPopulate = (dealId: string, opts: boolean | { fresh?: boolean; overwrite?: boolean } = false) =>
  api<{ peers: number; skipped?: string; enrichingIpo?: boolean }>(`/deals/${dealId}/peers`, {
    method: 'POST',
    body: JSON.stringify(typeof opts === 'boolean' ? { fresh: opts } : opts),
  });
// ── Company-registry (MCA) fetch ─────────────────────────────────────────────
export interface RegistryFetchResult { found: boolean; registry?: RegistryLookup | null; leadership?: Leader[]; triedSources?: string[]; message?: string; }
/** Resolve MCA registry + web leadership. `fresh` bypasses the 24h caches (forced refresh). */
export const registryFetch = (dealId: string, cin?: string, fresh = false) =>
  api<RegistryFetchResult>(`/deals/${dealId}/registry`, { method: 'POST', body: JSON.stringify({ ...(cin ? { cin } : {}), fresh }) });
// ── People (deck + MCA registry + web leadership, one pass) ──────────────────
export type PeopleSourceKey = 'deck' | 'mca' | 'web';
export interface PeopleSourceReport { status: 'ok' | 'none' | 'error' | 'skipped'; count: number; note?: string }
export interface PeopleFetchResult {
  ok: boolean;
  sources: Record<PeopleSourceKey, PeopleSourceReport>;
  added: number;
  team: TeamMemberBio[];
  registry?: RegistryLookup | null;
  leadership?: Leader[];
}
/** Refresh the deal's people from all three sources at once (deck ∪ MCA ∪ web). */
export const peopleFetch = (dealId: string, opts: { cin?: string; fresh?: boolean } = {}) =>
  api<PeopleFetchResult>(`/deals/${dealId}/people`, { method: 'POST', body: JSON.stringify({ ...(opts.cin ? { cin: opts.cin } : {}), fresh: opts.fresh ?? true }) });
// ── Probe42 (CHARGEABLE corporate-data API — admin-gated, explicit consent) ──
export interface Probe42GetResult { data: Probe42Data | null; configured: boolean; identifier: string | null; entityType: 'company' | 'llp' | 'pnp' | null; }
export interface Probe42FetchResult { data: Probe42Data; cached: boolean; chargeable: boolean; }
/** Read the CACHED Probe42 payload (free; never bills). Admin-only. */
export const probe42Get = (dealId: string) => api<Probe42GetResult>(`/deals/${dealId}/probe42`);
/** Trigger a Probe42 pull. `fresh:false` serves cache free; `fresh:true` is CHARGEABLE and needs consent:true. */
export const probe42Fetch = (dealId: string, opts: { fresh?: boolean; consent?: boolean; identifier?: string } = {}) =>
  api<Probe42FetchResult>(`/deals/${dealId}/probe42`, { method: 'POST', body: JSON.stringify({ fresh: opts.fresh ?? false, consent: opts.consent ?? false, ...(opts.identifier ? { identifier: opts.identifier } : {}) }) });
/** Request a fresh data update (CHARGEABLE, ~2–4h). Returns a request_id polled by n8n. */
export const probe42Refresh = (dealId: string, opts: { consent?: boolean; identifier?: string } = {}) =>
  api<{ requestId: string | null; note?: string }>(`/deals/${dealId}/probe42/refresh`, { method: 'POST', body: JSON.stringify({ consent: opts.consent ?? false, ...(opts.identifier ? { identifier: opts.identifier } : {}) }) });
/** Non-admins: ask an admin to run Probe42 for this deal (logged; never calls the API). */
export const probe42RequestAccess = (dealId: string) =>
  api<{ ok: boolean }>(`/deals/${dealId}/probe42/request-access`, { method: 'POST', body: JSON.stringify({}) });
// ── Section insight (short, strictly-grounded AI summary for one dashboard section) ─────────────
export type InsightSection = 'overview' | 'litigation' | 'gst' | 'financials' | 'valuation' | 'relatedParty' | 'compliance' | 'business';
export interface SectionInsightResult {
  summary: string;
  keyInsights: string[];
  risks: string[];
  positives: string[];
  metrics: Record<string, string>;
  insufficientData: boolean;
  fetchedAt: string;
}
/** Free — reuses the deal's own already-loaded data (registry/financials/cached Probe42 payload),
 *  never re-calls Probe42. `fresh:true` bypasses the 24h cache. Returns `{insight:null}` when
 *  there's nothing to summarize (caller should render no card, not an "insufficient data" one). */
export const insightFetch = (dealId: string, section: InsightSection, fresh = false) =>
  api<{ insight: SectionInsightResult | null }>(`/deals/${dealId}/insight?section=${section}`, { method: 'POST', body: JSON.stringify({ fresh }) });
export interface Probe42UsageEntry { id: string; action: string; diff: Record<string, unknown> | null; ts: string; actor: string | null; }
/** The audit trail of Probe42 calls for this deal (who/when/what). Admin-only. */
export const probe42Usage = (dealId: string) => api<{ usage: Probe42UsageEntry[] }>(`/deals/${dealId}/probe42/usage`);

// ── Probe42 access grants — the admin-only + approve-whom-to-add model (decision.md D15) ──
export interface Probe42AccessRequest { actorId: string | null; dealId: string | null; ts: string; name: string | null; email: string | null; role: string | null; company: string | null; }
export interface Probe42Grant { memberId: string; name: string | null; email: string | null; role: string | null; grantedAt: string; grantedBy: string | null; }
/** Admin console: who's asked (and isn't granted yet) + who's currently granted. */
export const getProbe42Access = () => api<{ pending: Probe42AccessRequest[]; granted: Probe42Grant[] }>('/probe42/access');
export const grantProbe42Access = (memberId: string) => api<{ ok: boolean }>(`/probe42/access/${memberId}/grant`, { method: 'POST', body: JSON.stringify({}) });
export const revokeProbe42Access = (memberId: string) => api<{ ok: boolean }>(`/probe42/access/${memberId}`, { method: 'DELETE' });

// ── Probe42 usage — fund-wide log of chargeable calls, since it's a paid API ──
export interface Probe42UsageCall {
  id: string; action: string; ts: string; actor: string | null;
  dealId: string | null; company: string | null; short: string | null;
  sandbox: boolean | null; // null = written before this flag was tracked (unknown)
}
export interface Probe42UsageSummary {
  totalCalls: number; productionCalls: number; sandboxCalls: number; unknownCalls: number;
  productionThisMonth: number; uniqueDealsCharged: number;
}
export const getProbe42Usage = () => api<{ summary: Probe42UsageSummary; calls: Probe42UsageCall[] }>('/probe42/usage');

// ── Industry & market deep-dive (re-runnable) ────────────────────────────────
export interface IndustryFetchResult { ok: boolean; subIndustry: string; industryDetail: IndustryDetail; creditsUsed: number; }
/** Run the deep-dive India-specific industry & market analysis; persists subIndustry + industryDetail. `fresh` bypasses the cache. */
export const industryFetch = (dealId: string, fresh = false) =>
  api<IndustryFetchResult>(`/deals/${dealId}/industry`, { method: 'POST', body: JSON.stringify({ fresh }) });
// ── Company storyboard: deck (+ optional web) profile (re-runnable) ──────────
export interface BusinessFetchResult { ok: boolean; businessDetail: BusinessDetail; docs: string[]; sources: string[]; sections: number; }
/** Rewrite the Business card / storyboard from the deal's deck text, in plain language. `withWeb` adds a bounded web search on top of the deck. */
export const businessFromDeck = (dealId: string, opts: { withWeb?: boolean } = {}) =>
  api<BusinessFetchResult>(`/deals/${dealId}/business`, { method: 'POST', body: JSON.stringify({ withWeb: opts.withWeb ?? false }) });
/** Fetch a document's extracted text (for viewing in-app). */
export const getDocumentText = (dealId: string, docId: string) =>
  api<{ name: string; text: string }>(`/deals/${dealId}/documents/${docId}/text`);
/** Run the full deck extraction on stored docs → fill empty Team/Business/Financials fields. */
export const extractFromDeck = (dealId: string, force = false) =>
  api<{ ok: boolean; filled: string[]; coverage: { team: number; customers: number; products: number; pnl: number; missing: string[]; warnings: string[] } }>(`/deals/${dealId}/documents/extract`, { method: 'POST', body: JSON.stringify({ force }) });
export interface IcVote { id: string; dealId: string; voterEmail: string; voterName: string; decision: 'approve' | 'pass' | 'abstain'; rationale: string | null; updatedAt: string; }
export interface IcMember { name: string; email: string | null; role: string; vote: IcVote | null; }
export interface IcState {
  votes: IcVote[]; members: IcMember[]; tally: { approve: number; pass: number; abstain: number };
  quorum: number; votersExpected: number; decision: string | null; decidedAt: string | null; decidedBy: string | null; me: string;
}
export const getIc = (id: string) => api<IcState>(`/deals/${id}/ic`);
export const castVote = (id: string, b: { decision: string; rationale?: string }) =>
  api<{ vote: IcVote }>(`/deals/${id}/ic/vote`, { method: 'POST', body: JSON.stringify(b) });
export const retractVote = (id: string) => api<{ ok: boolean }>(`/deals/${id}/ic/vote`, { method: 'DELETE' });
export const setIcDecision = (id: string, decision: string) =>
  api<{ decision: string | null; decidedAt: string | null; decidedBy: string | null }>(`/deals/${id}/ic/decision`, { method: 'POST', body: JSON.stringify({ decision }) });
export const getSettings = () => api<{ settings: Settings | null }>('/settings');
/** MCP connector status for the Settings panel; returns the tokens to admins so the UI can show the full config. */
export const getMcpInfo = () => api<{ enabled: boolean; tools: string[]; token?: string | null; cfClientId?: string | null; cfClientSecret?: string | null }>('/integrations/mcp');
/** Generate (or rotate) the MCP connector token. Admin only. Returns the new token. */
export const generateMcpToken = () => api<{ token: string }>('/integrations/mcp/token', { method: 'POST' });
/** Store the Cloudflare Access service-token pair so the UI can render the full copyable config. Admin only. */
export const saveMcpServiceToken = (clientId: string, clientSecret: string) =>
  api<{ ok: boolean }>('/integrations/mcp/service-token', { method: 'PUT', body: JSON.stringify({ clientId, clientSecret }) });
export const patchSettings = (patch: Partial<Settings>) =>
  api<{ settings: Settings }>('/settings', { method: 'PATCH', body: JSON.stringify(patch) });
export const importWorkspace = (data: unknown) =>
  api<{ ok: boolean; upserted: number }>('/import', { method: 'POST', body: JSON.stringify(data) });
